import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";

export const Route = createFileRoute("/accept-invite/$token")({
  component: AcceptInvitePage,
  head: () => ({ meta: [{ title: "Accept invitation — MissionReady" }] }),
});

type Preview = {
  organization_id: string;
  organization_name: string;
  email: string;
  role: string;
  accepted_at: string | null;
  expires_at: string | null;
};

function AcceptInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { session, refreshMemberships, setCurrentOrgId } = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await (supabase.rpc as any)("get_invite_by_token", { _token: token });
      if (!alive) return;
      if (error) { setError(friendlyAuthError(error)); setLoading(false); return; }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) setError("This invite is invalid or has been revoked.");
      else setPreview(row as Preview);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [token]);

  const accept = async () => {
    if (!preview) return;
    setAccepting(true);
    const { data, error } = await (supabase.rpc as any)("accept_invite", { _token: token });
    setAccepting(false);
    if (error) return toast.error(friendlyAuthError(error));
    toast.success(`Joined ${preview.organization_name}`);
    await refreshMemberships();
    if (data) setCurrentOrgId(data as string);
    navigate({ to: "/operations" });
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-hero px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading invite…</p>
          ) : error || !preview ? (
            <>
              <h1 className="font-display text-2xl font-semibold">Invite unavailable</h1>
              <p className="mt-2 text-sm text-muted-foreground">{error ?? "Unknown error"}</p>
              <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Back to sign in</Link>
            </>
          ) : preview.accepted_at ? (
            <>
              <h1 className="font-display text-2xl font-semibold">Already accepted</h1>
              <p className="mt-2 text-sm text-muted-foreground">This invitation has already been used.</p>
              <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Sign in</Link>
            </>
          ) : preview.expires_at && new Date(preview.expires_at) < new Date() ? (
            <>
              <h1 className="font-display text-2xl font-semibold">Invite expired</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This invitation expired on {new Date(preview.expires_at).toLocaleDateString()}. Ask your administrator to send a new one.
              </p>
              <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Back to sign in</Link>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-semibold">Join {preview.organization_name}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                You've been invited as <strong>{preview.role}</strong>. The invite was sent to <strong>{preview.email}</strong>.
              </p>
              {!session ? (
                <div className="mt-6 space-y-3">
                  <p className="text-sm">Sign in or create an account with <strong>{preview.email}</strong> to accept.</p>
                  <Link
                    to="/login"
                    search={{ next: `/accept-invite/${token}`, email: preview.email } as never}
                    className="block"
                  >
                    <Button className="w-full">Sign in</Button>
                  </Link>
                  <Link
                    to="/signup"
                    search={{ next: `/accept-invite/${token}`, email: preview.email } as never}
                    className="block"
                  >
                    <Button variant="outline" className="w-full">Create account</Button>
                  </Link>
                </div>
              ) : session.user.email?.toLowerCase() !== preview.email.toLowerCase() ? (
                <div className="mt-6 space-y-3">
                  <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
                    You're signed in as {session.user.email}. Sign out and sign back in as {preview.email} to accept this invite.
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => supabase.auth.signOut()}>Sign out</Button>
                </div>
              ) : (
                <div className="mt-6">
                  <Button className="w-full" onClick={accept} disabled={accepting}>
                    {accepting ? "Joining…" : `Join ${preview.organization_name}`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
